// Players moved from the café's previous system (F-HQ-40, F-IAM-13; spec/13 §8.2): the profiles a move from another
// platform brought over, waiting for their players to claim them, with the money and time held for each. Staff who
// may (`wallet:topup` and `players:id_check`) claim one at the counter from its row; the owner (`credit:write_off`)
// sees what nobody claimed in time and signs off its write-off. Shown only where a move brought players over.

import type { ImportedPlayer } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, EmptyState, Table, type TableColumn } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { SearchField, TableSkeleton, Toolbar } from '../../ui';
import { CounterClaimDialog } from './CounterClaimDialog';
import { useImportedPlayers } from './queries';
import { WriteOffPanel } from './WriteOffPanel';

/** A moved player's name as staff call them: their name, or their old username. */
function movedName(player: ImportedPlayer): string {
  const name = [player.firstName, player.lastName].filter((part): part is string => part !== null && part !== '').join(' ');
  return name === '' ? player.username : name;
}

/** The platforms the moved players came from, for the tab's name: one, or several. */
export function movedPlatformsOf(players: readonly ImportedPlayer[]): readonly ImportedPlayer['sourcePlatform'][] {
  return [...new Set(players.map((player) => player.sourcePlatform))];
}

const STATUS_TONE: Readonly<Record<ImportedPlayer['status'], 'accent' | 'ok' | 'neutral' | 'info'>> = {
  unclaimed: 'accent',
  claimed: 'ok',
  undone: 'neutral',
  erased: 'info',
};

export function MovedPlayers({ q, platform }: { readonly q: string; readonly platform: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const players = useImportedPlayers(scope.organizationId, scope.venueId, q);
  const access = useAccess(scope.access);
  const canClaim = access.status === 'ready' && access.can('wallet:topup') && access.can('players:id_check');
  const canWriteOff = access.status === 'ready' && access.can('credit:write_off');
  const [claiming, setClaiming] = useState<ImportedPlayer | null>(null);
  const day = (date: string) => format.date(`${date}T12:00:00.000Z`, 'medium');

  const columns = useMemo<readonly TableColumn<ImportedPlayer>[]>(
    () => [
      {
        key: 'player',
        header: t('players.moved.column.player'),
        cell: (player) => (
          <div className="console-people-cell">
            <Avatar name={movedName(player)} size="sm" decorative />
            <div className="console-people-cell__who">
              <strong className="console-moved__name">{movedName(player)}</strong>
              <span className="console-subtle">@{player.username}</span>
              <span className="console-people-tags">
                {player.minor ? <Badge tone="warn">{t('players.moved.minor')}</Badge> : null}
                {player.banned ? <Badge tone="danger">{t('players.moved.banned')}</Badge> : null}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: 'contact',
        header: t('players.moved.column.contact'),
        cell: (player) =>
          player.phone === null && player.email === null ? (
            <span className="console-subtle">{t('players.moved.noContact')}</span>
          ) : (
            <span className="console-people-cell__who">
              {player.phone === null ? null : <span>{player.phone}</span>}
              {player.email === null ? null : <span className="console-subtle">{player.email}</span>}
            </span>
          ),
      },
      {
        key: 'money',
        header: t('players.moved.column.money'),
        numeric: true,
        cell: (player) => (
          <span className="console-people-cell__who">
            <span className="console-money">{format.money(player.cash)}</span>
            {player.cashTaxPaid.amount > 0 ? (
              <span title={t('players.moved.gstPaidHint', { amount: format.money(player.cashTaxPaid) })}>
                <Badge tone="info">{t('players.moved.gstPaid')}</Badge>
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'time',
        header: t('players.moved.column.time'),
        numeric: true,
        cell: (player) =>
          player.timeSeconds === 0 ? (
            <span className="console-subtle">—</span>
          ) : (
            <span className="console-people-cell__who">
              <span className="console-money">{format.duration(player.timeSeconds)}</span>
              {player.timeValidUntil === null ? null : <span className="console-subtle">{t('players.moved.timeUntil', { date: day(player.timeValidUntil) })}</span>}
            </span>
          ),
      },
      {
        key: 'status',
        header: t('players.moved.column.status'),
        cell: (player) => (
          <span className="console-people-cell__who console-moved__status">
            <Badge tone={STATUS_TONE[player.status]}>{t(`players.moved.status.${player.status}`)}</Badge>
            {player.status === 'unclaimed' ? <span className="console-subtle">{t('players.moved.waitsUntil', { date: day(player.claimWindowEndsOn) })}</span> : null}
          </span>
        ),
      },
      { key: 'joined', header: t('players.moved.column.joined'), cell: (player) => (player.joinedOn === null ? <span className="console-subtle">—</span> : day(player.joinedOn)) },
      ...(canClaim
        ? [
            {
              key: 'action',
              header: t('players.moved.column.action'),
              cell: (player: ImportedPlayer) =>
                player.status === 'unclaimed' ? (
                  <Button size="sm" variant="secondary" aria-label={t('players.moved.claimButtonLabel', { player: movedName(player) })} onClick={() => setClaiming(player)}>
                    {t('players.moved.claimButton')}
                  </Button>
                ) : null,
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `day` closes over `format`, already a dependency.
    [t, format, canClaim],
  );

  if (players.data === undefined) {
    return players.isError ? (
      <LoadFailure error={players.error} message={t('players.moved.unavailable')} onRetry={() => void players.refetch()} />
    ) : (
      <TableSkeleton label={t('players.moved.loading')} />
    );
  }
  const rows = players.data.pages.flatMap((page) => page.data);

  return (
    <section aria-label={t('players.moved.caption')} className="console-moved">
      <p className="console-subtle">{t('players.moved.intro', { platform })}</p>
      {canWriteOff ? <WriteOffPanel /> : null}
      <CounterClaimDialog player={claiming} platform={platform} onClose={() => setClaiming(null)} />
      {rows.length === 0 ? (
        <EmptyState size="compact" icon={<Icon name="user" />} title={t('players.moved.empty', { query: q.trim() })} description={t('players.moved.emptyHint')} />
      ) : (
        <Table className="console-players-table" caption={t('players.moved.caption')} captionHidden columns={columns} rows={rows} rowKey={(player) => player.id} />
      )}
      {players.hasNextPage ? (
        <div className="console-list-foot">
          <Button variant="ghost" size="sm" busy={players.isFetchingNextPage} onClick={() => void players.fetchNextPage()}>
            {t('players.moved.more')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** The search field over the moved list. */
export function MovedSearch({ value, onChange }: { readonly value: string; readonly onChange: (value: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Toolbar label={t('players.moved.search.label')}>
      <SearchField label={t('players.moved.search.label')} placeholder={t('players.moved.search.placeholder')} value={value} onChange={(event) => onChange(event.target.value)} />
    </Toolbar>
  );
}

/** Whether there is a moved list to show at all here, and the platforms it came from: the list's first page. */
export function useMovedSummary(): { readonly shown: boolean; readonly platforms: readonly ImportedPlayer['sourcePlatform'][] } {
  const scope = useScopeIds();
  const probe = useImportedPlayers(scope.organizationId, scope.venueId, '');
  const rows = probe.data?.pages.flatMap((page) => page.data) ?? [];
  return { shown: rows.length > 0, platforms: movedPlatformsOf(rows) };
}
